import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Menu, X, Calendar } from "lucide-react";
import { SOLUTIONS, BRAND_LOGO_LIGHT_BG, CALENDLY_URL } from "../lib/site";

const navLinkClass = ({ isActive }) =>
  `text-[15px] tracking-wide transition-colors ${
    isActive ? "text-[#0A192F]" : "text-[#1A1D20] hover:text-[#0A192F]"
  } link-underline`;

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-sm border-b border-[#0A192F]/10"
      data-testid="site-header"
    >
      {/* Brand accent strip — threads the logo cyan into the rest of the page */}
      <div className="h-[3px] w-full bg-[#38BDF8]" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-32 lg:h-36 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center" data-testid="brand-link">
          <img
            src={BRAND_LOGO_LIGHT_BG}
            alt="Aligned Education Systems"
            className="h-28 sm:h-32 lg:h-36 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          <NavLink to="/" end className={navLinkClass} data-testid="nav-home">
            Home
          </NavLink>

          <div
            className="relative"
            onMouseEnter={() => setSolutionsOpen(true)}
            onMouseLeave={() => setSolutionsOpen(false)}
          >
            <button
              className="text-[15px] tracking-wide text-[#1A1D20] hover:text-[#0A192F] inline-flex items-center gap-1"
              data-testid="nav-solutions"
              onClick={() => setSolutionsOpen((v) => !v)}
            >
              Solutions <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
            </button>
            {solutionsOpen && (
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
                data-testid="solutions-dropdown"
              >
                <div className="w-[340px] bg-[#FDFBF7] border border-[#0A192F]/10 shadow-sm">
                  {SOLUTIONS.map((s) => (
                    <Link
                      key={s.slug}
                      to={s.path}
                      className="block px-6 py-4 hover:bg-[#F4F1EB] border-b border-[#0A192F]/5 last:border-b-0"
                      data-testid={`solutions-link-${s.slug}`}
                    >
                      <div className="font-serif-display text-lg text-[#0A192F]">
                        {s.name}
                      </div>
                      <div className="text-sm text-[#4A5568] mt-1">{s.tagline}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <NavLink to="/about" className={navLinkClass} data-testid="nav-about">
            About
          </NavLink>
          <NavLink to="/district-systems-overview" className={navLinkClass} data-testid="nav-districts">
            For Districts
          </NavLink>
          <NavLink to="/alignment-check" className={navLinkClass} data-testid="nav-alignment-check">
            Alignment Check
          </NavLink>
          <NavLink to="/insights" className={navLinkClass} data-testid="nav-insights">
            Insights
          </NavLink>
          <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact">
            Contact
          </NavLink>
        </nav>

        <div className="hidden lg:block">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-6 py-3 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
            data-testid="header-walkthrough-btn"
          >
            <Calendar className="w-4 h-4" strokeWidth={1.5} /> Schedule a Walkthrough
          </a>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-[#0A192F]/10 bg-[#FDFBF7]" data-testid="mobile-nav">
          <div className="px-6 py-4 flex flex-col gap-4">
            <Link to="/" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-home">Home</Link>
            <div className="pt-2 text-xs uppercase tracking-widest text-[#4A5568]">Solutions</div>
            {SOLUTIONS.map((s) => (
              <Link
                key={s.slug}
                to={s.path}
                onClick={() => setMobileOpen(false)}
                className="pl-3 border-l border-[#0A192F]/10 text-[#0A192F]"
                data-testid={`mobile-nav-${s.slug}`}
              >
                {s.name}
              </Link>
            ))}
            <Link to="/about" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-about">About</Link>
            <Link to="/district-systems-overview" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-districts">For Districts</Link>
            <Link to="/alignment-check" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-alignment-check">Alignment Check</Link>
            <Link to="/insights" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-insights">Insights</Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-contact">Contact</Link>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-6 py-3 text-sm tracking-wide border border-[#0A192F]"
              data-testid="mobile-walkthrough-btn"
            >
              <Calendar className="w-4 h-4" strokeWidth={1.5} /> Schedule a Walkthrough
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
